/**
 * REVIEW FF-004 — application-level error boundary.
 *
 * React Router's `errorElement` catches errors thrown in loaders/actions and
 * during render of route children. We use this component at the router root so
 * any uncaught render throw (lazy chunk load fail, `useSortable` crash inside
 * the ColumnManagerDrawer, `parseKpiEntries` crash on a malformed payload,
 * etc.) renders a controlled fallback instead of React's white screen.
 *
 * Also used directly via `<ErrorBoundary>` wrappers around feature subtrees
 * where finer-grained recovery is desired.
 */

import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/** Router-level fallback. Mount via `errorElement` on a route. */
export function RootErrorFallback(): JSX.Element {
  const err = useRouteError();
  let title = 'Something went wrong.';
  let detail: string | null = null;

  if (isRouteErrorResponse(err)) {
    title = `${String(err.status)} ${err.statusText || ''}`.trim();
    if (typeof err.data === 'string') detail = err.data;
  } else if (err instanceof Error) {
    detail = err.message;
  }

  return (
    <div
      role="alert"
      data-testid="root-error-fallback"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <h1 className="text-xl font-semibold">{title}</h1>
      {detail !== null && detail !== '' && (
        <p className="max-w-md text-sm text-muted-foreground">{detail}</p>
      )}
      <p className="text-sm text-muted-foreground">
        Refresh the page. If the issue persists, contact your administrator.
      </p>
      <Button
        type="button"
        onClick={() => {
          window.location.reload();
        }}
      >
        Refresh
      </Button>
    </div>
  );
}

interface BoundaryProps {
  readonly children: ReactNode;
  /** Optional override fallback. Falls back to a generic panel. */
  readonly fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface BoundaryState {
  readonly error: Error | null;
}

/** Class-based error boundary for subtree-level recovery (e.g. inside AppShell). */
export class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to console; production telemetry can pick it up via window.onerror.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div
          role="alert"
          data-testid="subtree-error-fallback"
          className="flex flex-col items-center justify-center gap-3 p-6 text-center"
        >
          <h2 className="text-lg font-semibold">Something went wrong on this page.</h2>
          <p className="max-w-md text-sm text-muted-foreground">{error.message}</p>
          <Button type="button" onClick={this.reset}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
